<template>
  <div
    class="unit-tabs"
    role="tablist"
    :aria-label="label"
    aria-orientation="horizontal"
    @keydown="onKeydown"
  >
    <button
      v-for="tab in tabs"
      :id="tabId(tab.id)"
      :key="tab.id"
      ref="tabButtons"
      type="button"
      role="tab"
      class="unit-tabs__tab"
      :class="{ 'unit-tabs__tab--active': tab.id === modelValue }"
      :aria-selected="String(tab.id === modelValue)"
      :aria-controls="panelIdPrefix ? panelId(tab.id) : undefined"
      :tabindex="tab.id === activeFocusId ? 0 : -1"
      :disabled="tab.disabled || undefined"
      @click="select(tab)"
    >
      {{ tab.label }}
    </button>
  </div>
</template>

<script>
import { computed, ref } from "vue";

// Minimal navigation tabs: text only, the active tab carries the accent colour
// and a heavier weight (so selection never relies on colour alone). This is a
// navigation contract (tablist/tab), distinct from SingleSelectToggleRow, which
// selects a value. Arrow keys, Home and End move and activate the selection.
export default {
  name: "UnitTabs",
  props: {
    tabs: {
      type: Array,
      required: true,
      validator: (value) =>
        value.every((tab) => typeof tab?.id === "string" && typeof tab?.label === "string"),
    },
    modelValue: { type: String, default: null },
    label: { type: String, required: true },
    idPrefix: { type: String, default: "unit-tabs" },
    // When set, each tab points at `${panelIdPrefix}-${tab.id}` via aria-controls;
    // the caller gives its panels those ids and role="tabpanel".
    panelIdPrefix: { type: String, default: null },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const tabButtons = ref([]);
    const enabledTabs = computed(() => props.tabs.filter((tab) => !tab.disabled));
    const activeFocusId = computed(() =>
      enabledTabs.value.some((tab) => tab.id === props.modelValue)
        ? props.modelValue
        : enabledTabs.value[0]?.id ?? null
    );
    const tabId = (id) => `${props.idPrefix}-tab-${id}`;
    const panelId = (id) => `${props.panelIdPrefix}-${id}`;

    const select = (tab) => {
      if (tab.disabled || tab.id === props.modelValue) return;
      emit("update:modelValue", tab.id);
    };

    const focusTab = (tab) => {
      const index = props.tabs.findIndex((entry) => entry.id === tab.id);
      tabButtons.value[index]?.focus();
    };

    const onKeydown = (event) => {
      const tabs = enabledTabs.value;
      if (tabs.length === 0) return;
      const current = tabs.findIndex((tab) => tab.id === activeFocusId.value);
      let next = null;
      if (event.key === "ArrowRight") next = tabs[(current + 1) % tabs.length];
      else if (event.key === "ArrowLeft") next = tabs[(current - 1 + tabs.length) % tabs.length];
      else if (event.key === "Home") next = tabs[0];
      else if (event.key === "End") next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next);
      focusTab(next);
    };

    return { activeFocusId, onKeydown, panelId, select, tabButtons, tabId };
  },
};
</script>

<style scoped>
.unit-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-5);
  min-width: 0;
}

.unit-tabs__tab {
  padding: var(--space-1) 0;
  border: 0;
  background: transparent;
  color: var(--color-text-secondary);
  font: inherit;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-normal);
  white-space: nowrap;
  cursor: pointer;
}

.unit-tabs__tab:hover:not(:disabled) {
  color: var(--color-text-primary);
}

.unit-tabs__tab--active,
.unit-tabs__tab--active:hover {
  color: var(--color-accent-primary);
  font-weight: var(--font-weight-semibold);
}

.unit-tabs__tab:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
  border-radius: var(--radius-xs);
}

.unit-tabs__tab:disabled {
  color: var(--color-text-muted);
  cursor: not-allowed;
}
</style>
